/**
 * Requirement spec fields shared by the New requirement review step (NewRequest.tsx) and the
 * requirement page's Edit form (RequisitionDetail.tsx) — #237/#238. One budget editor (currency,
 * min, max, per month / per year), one location editor (country, city, work mode), one experience
 * field, and the AI skill-relevance check, so both screens behave the same.
 */
import { useCallback, useState, type ReactNode } from "react";
import { SALARY_CURRENCIES, type SalaryCurrency } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { Dropdown } from "../../components/Dropdown.js";
import { SearchableSelect } from "../../components/SearchableSelect.js";
import { parseInrAmount } from "./requisitionFormat.js";

export const EXPERIENCE_PRESETS = ["0-1 Years", "1-3 Years", "3+ Years", "3-5 Years", "5+ Years", "5-8 Years", "7+ Years", "10+ Years"];

export const COUNTRY_OPTIONS = [
  "India",
  "United States",
  "United Kingdom",
  "United Arab Emirates",
  "Singapore",
  "Germany",
  "Netherlands",
  "Canada",
  "Australia",
];

const CITY_SUGGESTIONS: Record<string, string[]> = {
  India: ["Bangalore", "Chennai", "Hyderabad", "Pune", "Mumbai", "Delhi NCR", "Noida", "Gurgaon", "Kochi", "Coimbatore"],
  "United States": ["New York, NY", "San Francisco, CA", "Seattle, WA", "Austin, TX", "Chicago, IL", "Boston, MA"],
  "United Kingdom": ["London", "Manchester", "Edinburgh"],
  "United Arab Emirates": ["Dubai", "Abu Dhabi"],
  Singapore: ["Singapore"],
};

export const COUNTRY_CURRENCY: Record<string, SalaryCurrency> = {
  India: "INR",
  "United States": "USD",
  "United Kingdom": "GBP",
  "United Arab Emirates": "AED",
  Singapore: "SGD",
  Germany: "EUR",
  Netherlands: "EUR",
};

const COUNTRY_ALIASES: Record<string, string> = {
  uk: "United Kingdom",
  "united kingdom": "United Kingdom",
  usa: "United States",
  us: "United States",
  "united states": "United States",
  uae: "United Arab Emirates",
  india: "India",
  singapore: "Singapore",
};

export type WorkMode = "Remote" | "Hybrid" | "On-site";

export const WORK_MODES: { value: WorkMode; label: string }[] = [
  { value: "Remote", label: "Remote" },
  { value: "Hybrid", label: "Hybrid" },
  { value: "On-site", label: "Work from office" },
];

/** "On-site" is stored (older configs say so); people read "Work from office". */
export const workModeLabel = (mode: string): string => WORK_MODES.find((m) => m.value === mode)?.label ?? mode;

/** "Chennai, India (Hybrid)" → "Chennai, India". */
export const placeOnly = (loc: string): string => loc.replace(/\s*\((?:Remote|Hybrid|On-site)\)/gi, "").trim();

/** "Chicago, IL" → { city: "Chicago, IL", country: "United States" }; "Pune, India" → { Pune, India }. */
export function splitLocation(loc: string, fallbackCountry: string): { city: string; country: string } {
  const parts = placeOnly(loc).split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { city: "", country: fallbackCountry };
  const last = parts[parts.length - 1]!;
  if (/^[A-Z]{2}$/.test(last) && last !== "UK") return { city: parts.join(", "), country: "United States" };
  const country = COUNTRY_ALIASES[last.toLowerCase()] ?? (COUNTRY_OPTIONS.includes(last) ? last : null);
  if (country) return { city: parts.slice(0, -1).join(", "), country };
  return { city: parts.join(", "), country: fallbackCountry };
}

/** "Chicago, IL" + "United States" stays "Chicago, IL"; "Pune" + "India" → "Pune, India". */
export function joinLocation(city: string, country: string): string {
  const c = city.trim();
  const k = country.trim();
  if (!c) return k;
  if (!k || (k === "United States" && /,\s*[A-Z]{2}$/.test(c)) || c.toLowerCase().endsWith(k.toLowerCase())) return c;
  return `${c}, ${k}`;
}

/** Whole currency units from a typed amount ("₹ 14,00,000" → "1400000"). */
export const amountOf = (s: string): string => s.replace(/[^0-9]/g, "");

export const formatMoney = (amount: number, currency: SalaryCurrency): string =>
  new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

export interface Budget {
  currency: SalaryCurrency;
  min: string;
  max: string;
  period: "month" | "year";
}

const isCurrency = (c: unknown): c is SalaryCurrency => (SALARY_CURRENCIES as readonly unknown[]).includes(c);

/**
 * A saved requirement's budget, from the structured salary fields when present, else the typed
 * budget strings ("₹ 14,00,000", "24L", "$ 145,000"). Never invents an amount — blank stays blank.
 */
export function budgetFromConfig(cfg: Record<string, unknown>, fallbackCurrency: SalaryCurrency): Budget {
  const textHint = `${String(cfg.budgetMin ?? "")}${String(cfg.budgetMax ?? "")}`;
  const currency: SalaryCurrency = isCurrency(cfg.salaryCurrency)
    ? cfg.salaryCurrency
    : textHint.includes("₹")
      ? "INR"
      : textHint.includes("$")
        ? "USD"
        : fallbackCurrency;
  const read = (structured: unknown, typed: unknown): string => {
    if (typeof structured === "number" && structured > 0) return String(Math.round(structured));
    if (typed === undefined || typed === null || typed === "") return "";
    if (currency === "INR") {
      const n = parseInrAmount(typed as string | number);
      return n ? String(Math.round(n)) : "";
    }
    return amountOf(String(typed));
  };
  return {
    currency,
    min: read(cfg.salaryMin, cfg.budgetMin),
    max: read(cfg.salaryMax, cfg.budgetMax),
    period: cfg.salaryPeriod === "month" ? "month" : "year",
  };
}

/** The config fields a budget is saved as — structured numbers plus the display strings. */
export function budgetToConfig(b: Budget): Record<string, unknown> {
  const min = Number(b.min) || 0;
  const max = Number(b.max) || 0;
  return {
    salaryCurrency: b.currency,
    salaryPeriod: b.period,
    ...(min > 0 ? { salaryMin: min, budgetMin: formatMoney(min, b.currency) } : {}),
    ...(max > 0 ? { salaryMax: max, budgetMax: formatMoney(max, b.currency) } : {}),
  };
}

export function budgetProblem(b: Budget): string | null {
  const min = Number(b.min) || 0;
  const max = Number(b.max) || 0;
  return min > 0 && max > 0 && min > max ? "The minimum is higher than the maximum." : null;
}

const CURRENCY_OPTIONS = SALARY_CURRENCIES.map((c) => ({ value: c, label: c }));
const PERIOD_OPTIONS: { value: Budget["period"]; label: string }[] = [
  { value: "month", label: "per month" },
  { value: "year", label: "per year" },
];

export function BudgetFields({ budget, onChange }: { budget: Budget; onChange: (b: Budget) => void }): ReactNode {
  const min = Number(budget.min) || 0;
  const max = Number(budget.max) || 0;
  const problem = budgetProblem(budget);
  return (
    <>
      <div className="nr-budget-row">
        <Dropdown
          ariaLabel="Currency"
          value={budget.currency}
          options={CURRENCY_OPTIONS}
          onChange={(currency) => onChange({ ...budget, currency })}
        />
        <input
          type="number"
          min={0}
          className="text-input-field"
          aria-label="Minimum amount"
          placeholder="Min budget"
          value={budget.min}
          onChange={(e) => onChange({ ...budget, min: amountOf(e.target.value) })}
        />
        <input
          type="number"
          min={0}
          className="text-input-field"
          aria-label="Maximum amount"
          placeholder="Max budget"
          value={budget.max}
          onChange={(e) => onChange({ ...budget, max: amountOf(e.target.value) })}
        />
        <Dropdown
          ariaLabel="Period"
          value={budget.period}
          options={PERIOD_OPTIONS}
          onChange={(period) => onChange({ ...budget, period })}
        />
      </div>
      {(problem || min > 0 || max > 0) && (
        <p className={`mut sm nr-hint${problem ? " nr-hint-error" : ""}`}>
          {problem ??
            `${[min > 0 && formatMoney(min, budget.currency), max > 0 && formatMoney(max, budget.currency)].filter(Boolean).join(" – ")} ${budget.period === "month" ? "per month" : "per year"}`}
        </p>
      )}
    </>
  );
}

/**
 * Pick a preset or type your own ("4-6 Years"). Same type-or-pick dropdown as Department/Industry —
 * a native <datalist> only listed presets matching the text already in the box, so with "5+ Years"
 * filled in it opened as an empty popup. `id` is kept for callers' label wiring.
 */
export function ExperienceInput({ id, value, onChange }: { id: string; value: string; onChange: (v: string) => void }): ReactNode {
  return (
    <div id={id}>
      <SearchableSelect options={EXPERIENCE_PRESETS} value={value} onChange={onChange} placeholder="e.g. 5+ Years" />
    </div>
  );
}

export function LocationFields({
  idPrefix,
  place,
  onPlaceChange,
  mode,
  onModeChange,
  recentLocations = [],
}: {
  idPrefix: string;
  place: { city: string; country: string };
  /** `countryChanged` lets the caller follow the country with a currency. */
  onPlaceChange: (next: { city: string; country: string }, countryChanged: boolean) => void;
  mode: string;
  onModeChange: (m: WorkMode) => void;
  recentLocations?: string[];
}): ReactNode {
  const recent = recentLocations
    .map((l) => splitLocation(l, place.country))
    .filter((l) => l.country === place.country && l.city)
    .map((l) => l.city);
  const cities = [...new Set([...recent, ...(CITY_SUGGESTIONS[place.country] ?? [])])];
  return (
    <>
      <div className="nr-location-row">
        <SearchableSelect
          options={COUNTRY_OPTIONS}
          value={place.country}
          onChange={(val) => onPlaceChange({ country: val, city: val === place.country ? place.city : "" }, val !== place.country)}
          placeholder="Country…"
        />
        {/* Type-or-pick dropdown, not a native <datalist> — that only listed cities matching the
            text already in the box, so a filled-in city opened a one-item popup. */}
        <div id={`${idPrefix}-city`} aria-label="City">
          <SearchableSelect
            options={cities}
            value={place.city}
            onChange={(val) => onPlaceChange({ ...place, city: val }, false)}
            placeholder="City…"
          />
        </div>
      </div>
      {/* One-of-three choice → the same segmented control as We source / I'll source, so a
          selection never looks like a primary button next to plain ones. */}
      <div className="nr-segmented nr-mode-row" role="radiogroup" aria-label="Work mode">
        {WORK_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            role="radio"
            className={`nr-segment${mode === m.value ? " on" : ""}`}
            aria-checked={mode === m.value}
            onClick={() => onModeChange(m.value)}
          >
            {m.label}
          </button>
        ))}
      </div>
    </>
  );
}

/** A skill the recruiter added, as the AI relevance check judged it. Skills with no entry are
 *  approved (drafted by the requirement or already saved). */
export type SkillCheck =
  | { status: "checking" }
  | { status: "rejected"; reason: string }
  | { status: "unchecked"; reason: string };

export interface SkillRole {
  title: string;
  department?: string;
  industry?: string;
  experience?: string;
  location?: string;
  workplaceMode?: string;
  skills?: string[];
  summary?: string;
}

const UNAVAILABLE = "The relevance check is unavailable right now — added without a check.";

/**
 * The AI skill-relevance check (#237/#238, POST /requisitions/requirement/skill-check). Relevant →
 * no entry (an ordinary skill). Irrelevant → "rejected": shown red, never saved. Unavailable →
 * "unchecked": added, marked so (degrade, never block).
 */
export function useSkillChecks(): {
  checks: Record<string, SkillCheck>;
  check: (skill: string, role: SkillRole) => Promise<void>;
  forget: (skill: string) => void;
  reset: () => void;
  approved: (skills: string[]) => string[];
  checking: (skills: string[]) => boolean;
} {
  const [checks, setChecks] = useState<Record<string, SkillCheck>>({});
  const check = useCallback(async (skill: string, role: SkillRole) => {
    setChecks((prev) => ({ ...prev, [skill]: { status: "checking" } }));
    let verdict: SkillCheck | null = null;
    try {
      const res = await api.post<{ available: boolean; relevant?: boolean; reason?: string }>(
        "/requisitions/requirement/skill-check",
        { role: { ...role, skills: (role.skills ?? []).slice(0, 30), summary: role.summary?.slice(0, 8000) }, skill },
      );
      if (res.available && res.relevant === false) verdict = { status: "rejected", reason: res.reason || "Not relevant to this role." };
      else if (!res.available) verdict = { status: "unchecked", reason: UNAVAILABLE };
    } catch {
      verdict = { status: "unchecked", reason: UNAVAILABLE };
    }
    setChecks((prev) => {
      if (!(skill in prev)) return prev; // removed while it was being checked
      const next = { ...prev };
      if (verdict) next[skill] = verdict;
      else delete next[skill];
      return next;
    });
  }, []);
  const forget = useCallback((skill: string) => {
    setChecks((prev) => {
      const next = { ...prev };
      delete next[skill];
      return next;
    });
  }, []);
  return {
    checks,
    check,
    forget,
    reset: () => setChecks({}),
    approved: (skills) => skills.filter((s) => checks[s]?.status !== "rejected"),
    checking: (skills) => skills.some((s) => checks[s]?.status === "checking"),
  };
}

/** The skill tags with their check state; red = not relevant, will not be saved. */
export function CheckedSkillChips({
  skills,
  checks,
  onRemove,
}: {
  skills: string[];
  checks: Record<string, SkillCheck>;
  onRemove: (skill: string) => void;
}): ReactNode {
  const anyRejected = skills.some((s) => checks[s]?.status === "rejected");
  return (
    <>
      <div className="chat-active-skills-wrap">
        {skills.map((s) => {
          const c = checks[s];
          const cls =
            c?.status === "rejected"
              ? " nr-skill-rejected"
              : c?.status === "checking"
                ? " nr-skill-checking"
                : c?.status === "unchecked"
                  ? " nr-skill-unchecked"
                  : "";
          const tip =
            c?.status === "rejected"
              ? `Not relevant — will not be added. ${c.reason}`
              : c?.status === "checking"
                ? "Checking relevance…"
                : c?.status === "unchecked"
                  ? c.reason
                  : undefined;
          return (
            <span key={s} className={`skill-tag-pill${cls}`} data-tip={tip} data-tip-pos="top">
              <span>{s}</span>
              {c?.status === "checking" && <span className="nr-skill-status">checking…</span>}
              {c?.status === "rejected" && <span className="nr-skill-status">not relevant</span>}
              <button type="button" className="skill-remove-x" onClick={() => onRemove(s)} aria-label={`Remove ${s}`}>
                ×
              </button>
            </span>
          );
        })}
      </div>
      {anyRejected && (
        <p className="mut sm nr-hint nr-hint-error" role="status">
          Red skills aren&rsquo;t relevant to this role and will not be added to the screening criteria. Remove them with ×.
        </p>
      )}
    </>
  );
}
